/**
 * Helper mínimo de IndexedDB para la Capa Operacional AI-First.
 *
 * Provee un almacén append-only de registros ordenados con una **clave
 * autoincremental** que actúa como secuencia global (`seq_global`). El
 * objetivo es soportar el Event_Store (`eventStore.ts`) en el navegador
 * (IndexedDB + `crypto.subtle`) y, cuando IndexedDB **no** está
 * disponible (entornos Node/Vitest/jsdom sin `indexedDB`, modo privado,
 * etc.), conmutar de forma transparente a un **fallback en memoria** con
 * idéntica semántica de orden de inserción.
 *
 * El helper es deliberadamente genérico y sin dependencias: no conoce el
 * esquema del Evento_Operacional, solo persiste registros opacos en
 * orden de llegada y los devuelve en ese mismo orden.
 */

/** Registro persistido con su secuencia global (1-based, orden de inserción). */
export interface StoredRecord<T> {
  /** Secuencia global monótona asignada en el momento del `add` (1-based). */
  seq_global: number;
  /** Valor opaco persistido (en el Event_Store, un Evento_Operacional). */
  value: T;
}

/**
 * Almacén append-only de registros ordenados.
 *
 * Contrato:
 *  - `add` asigna la siguiente `seq_global` y persiste el registro.
 *  - `getAll` devuelve **todos** los registros en orden ascendente de
 *    `seq_global` (orden de inserción).
 *  - No se expone `update` ni `delete`: el almacén es append-only por
 *    construcción (R9.2).
 *  - `count` devuelve el número de registros persistidos.
 *  - `clear` vacía el almacén (uso de test/seed, nunca expuesto a la UI).
 */
export interface AppendOnlyStore<T> {
  /** Indica si el respaldo activo es IndexedDB (`true`) o memoria (`false`). */
  readonly persistent: boolean;
  add(value: T): Promise<StoredRecord<T>>;
  getAll(): Promise<Array<StoredRecord<T>>>;
  count(): Promise<number>;
  clear(): Promise<void>;
}

const DB_NAME = "anc-event-store";
const DB_VERSION = 1;

/** Detecta si IndexedDB está disponible en el entorno actual. */
export function indexedDbAvailable(): boolean {
  try {
    return typeof globalThis !== "undefined" && typeof globalThis.indexedDB !== "undefined" && globalThis.indexedDB !== null;
  } catch {
    return false;
  }
}

/** Promisifica una `IDBRequest`. */
function promisifyRequest<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("IndexedDB request failed"));
  });
}

/** Implementación en memoria (fallback para Node/Vitest/jsdom). */
class InMemoryStore<T> implements AppendOnlyStore<T> {
  readonly persistent = false;
  private readonly records: Array<StoredRecord<T>> = [];

  async add(value: T): Promise<StoredRecord<T>> {
    const record: StoredRecord<T> = { seq_global: this.records.length + 1, value };
    this.records.push(record);
    return { ...record };
  }

  async getAll(): Promise<Array<StoredRecord<T>>> {
    // Copia defensiva en orden de inserción.
    return this.records.map((r) => ({ ...r }));
  }

  async count(): Promise<number> {
    return this.records.length;
  }

  async clear(): Promise<void> {
    this.records.length = 0;
  }
}

/** Implementación respaldada por IndexedDB. */
class IndexedDbStore<T> implements AppendOnlyStore<T> {
  readonly persistent = true;

  constructor(
    private readonly db: IDBDatabase,
    private readonly storeName: string,
  ) {}

  private tx(mode: IDBTransactionMode): IDBObjectStore {
    return this.db.transaction(this.storeName, mode).objectStore(this.storeName);
  }

  async add(value: T): Promise<StoredRecord<T>> {
    const store = this.tx("readwrite");
    // La clave es autoincremental: actúa como `seq_global` 1-based.
    const key = (await promisifyRequest(store.add({ value }))) as number;
    return { seq_global: key, value };
  }

  async getAll(): Promise<Array<StoredRecord<T>>> {
    const store = this.tx("readonly");
    const raw = (await promisifyRequest(store.getAll())) as Array<{ value: T }>;
    const keys = (await promisifyRequest(store.getAllKeys())) as IDBValidKey[];
    return raw.map((r, i) => ({ seq_global: Number(keys[i]), value: r.value }));
  }

  async count(): Promise<number> {
    return promisifyRequest(this.tx("readonly").count());
  }

  async clear(): Promise<void> {
    await promisifyRequest(this.tx("readwrite").clear());
  }
}

/**
 * Abre (o crea) un {@link AppendOnlyStore} respaldado por IndexedDB.
 *
 * Si IndexedDB no está disponible o falla la apertura, devuelve un
 * almacén en memoria con la misma semántica, de modo que el Event_Store
 * sea siempre testeable y funcional sin IndexedDB real.
 */
export async function openAppendOnlyStore<T>(storeName: string): Promise<AppendOnlyStore<T>> {
  if (!indexedDbAvailable()) {
    return new InMemoryStore<T>();
  }
  try {
    const db = await new Promise<IDBDatabase>((resolve, reject) => {
      const request = globalThis.indexedDB.open(DB_NAME, DB_VERSION);
      request.onupgradeneeded = () => {
        const database = request.result;
        if (!database.objectStoreNames.contains(storeName)) {
          database.createObjectStore(storeName, { keyPath: "seq_global", autoIncrement: true });
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error ?? new Error("No se pudo abrir IndexedDB"));
    });
    if (!db.objectStoreNames.contains(storeName)) {
      // La versión existente no tiene el store esperado: usa memoria para no corromper.
      db.close();
      return new InMemoryStore<T>();
    }
    return new IndexedDbStore<T>(db, storeName);
  } catch {
    return new InMemoryStore<T>();
  }
}
